import type { InvitaeFormState } from '../formData'
import {
  CARDIOLOGY_SPECIMEN_TYPES,
  CARDIOLOGY_PRIMARY_INDICATIONS,
  CARDIOLOGY_TEST_PANELS,
  CLINICAL_DIAGNOSES,
  CLINICAL_INFO_CATEGORIES,
} from '../cardiologyFormData'
import { LAB_ACCOUNT_SYSTEMS, INVITAE_PREFERRED_COLLECTION_KIT } from '../formData'
import type { OrderPdfData, OrderPdfTest, OrderPdfSection, AutoPopulateData } from './pdfTypes'

export function mapInvitaeCardiologyToPdf(
  form: InvitaeFormState,
  auto: AutoPopulateData,
): OrderPdfData {
  const cardiac = form.cardiacHistory

  // Resolve selected test panels
  const allTests = CARDIOLOGY_TEST_PANELS.flatMap((cat) => cat.tests)
  const selectedTests: OrderPdfTest[] = form.selectedTests
    .map((code) => {
      const found = allTests.find((t) => t.code === code)
      if (!found) return null
      const category = CARDIOLOGY_TEST_PANELS.find((cat) =>
        cat.tests.some((t) => t.code === code),
      )?.category
      return { code: found.code, name: found.name, category, geneList: found.geneList }
    })
    .filter((t): t is OrderPdfTest => t !== null)

  if (form.individualGenes) {
    selectedTests.push({
      code: 'CUSTOM',
      name: `Individual Genes: ${form.individualGenes}`,
      category: 'Custom',
    })
  }

  const vendorSections: OrderPdfSection[] = []

  // Kit shipping — Invitae ships the collection kit to the patient
  const shipAddress = form.kitShippingAddress || auto.patient.address
  const shipCityStateZip = form.kitShippingCityStateZip || auto.patient.cityStateZip
  const kitShippingData: Record<string, string> = {}
  if (selectedTests.length > 0) kitShippingData['Collection Kit'] = INVITAE_PREFERRED_COLLECTION_KIT
  if (shipAddress) kitShippingData['Shipping Address'] = shipAddress
  if (shipCityStateZip) kitShippingData['City, State, ZIP'] = shipCityStateZip
  if (form.kitShippingPhoneType) kitShippingData['Phone Type'] = form.kitShippingPhoneType
  if (form.kitShippingLanguagePreference)
    kitShippingData['Language Preference'] = form.kitShippingLanguagePreference
  if (Object.keys(kitShippingData).length > 0) {
    vendorSections.push({
      title: 'Kit Shipping Information',
      type: 'keyValue',
      data: kitShippingData,
    })
  }

  // Specimen
  const specimenLabel =
    CARDIOLOGY_SPECIMEN_TYPES.find((s) => s.value === form.specimenType)?.label || form.specimenType
  const specimenData: Record<string, string> = {}
  if (specimenLabel) specimenData['Specimen Type'] = specimenLabel
  if (form.dnaSource) specimenData['DNA Source'] = form.dnaSource
  if (form.specimenCollectionDate) specimenData['Collection Date'] = form.specimenCollectionDate
  if (form.specimenId) specimenData['Specimen ID'] = form.specimenId
  if (Object.keys(specimenData).length > 0) {
    vendorSections.push({ title: 'Specimen Information', type: 'keyValue', data: specimenData })
  }

  // Primary indication
  const indication =
    CARDIOLOGY_PRIMARY_INDICATIONS.find((i) => i.value === form.primaryIndication)?.label ||
    form.primaryIndication
  if (indication) {
    vendorSections.push({
      title: 'Primary Indication',
      type: 'text',
      content:
        indication + (form.primaryIndicationOther ? ` - ${form.primaryIndicationOther}` : ''),
    })
  }

  // Cardiac clinical history
  if (cardiac) {
    const historyData: Record<string, string> = {}
    if (cardiac.isAffected)
      historyData['Patient Affected'] = cardiac.isAffected === 'yes' ? 'Yes' : 'No'
    if (cardiac.ageAtDiagnosis) historyData['Age at Diagnosis'] = cardiac.ageAtDiagnosis
    if (cardiac.briefDescription) historyData['Brief Description'] = cardiac.briefDescription
    if (cardiac.hasHematologicalMalignancy)
      historyData['Hematological Malignancy'] =
        cardiac.hasHematologicalMalignancy === 'yes' ? 'Yes' : 'No'
    if (cardiac.hasPriorGeneticTesting) {
      historyData['Prior Genetic Testing'] = cardiac.hasPriorGeneticTesting === 'yes' ? 'Yes' : 'No'
      if (cardiac.priorGeneticTestResults)
        historyData['Prior Test Results'] = cardiac.priorGeneticTestResults
    }
    if (Object.keys(historyData).length > 0) {
      vendorSections.push({
        title: 'Cardiac Clinical History',
        type: 'keyValue',
        data: historyData,
      })
    }

    // Clinical diagnoses
    if (cardiac.clinicalDiagnoses.length > 0) {
      const diagLabels = cardiac.clinicalDiagnoses.map((v) => {
        const label = CLINICAL_DIAGNOSES.find((d) => d.value === v)?.label || v
        if (v === 'taad' && cardiac.taadDetails) return `${label}: ${cardiac.taadDetails}`
        if (v === 'other' && cardiac.clinicalDiagnosisOther)
          return `Other: ${cardiac.clinicalDiagnosisOther}`
        return label
      })
      if (cardiac.clinicalDiagnosisOther && !cardiac.clinicalDiagnoses.includes('other'))
        diagLabels.push(`Other: ${cardiac.clinicalDiagnosisOther}`)
      vendorSections.push({ title: 'Clinical Diagnoses', type: 'list', items: diagLabels })
    }

    // Clinical information categories
    for (const category of CLINICAL_INFO_CATEGORIES) {
      const status = cardiac.categoryStatus[category.category]
      if (!status || status === '' || status === 'no') continue

      const selectedItemValues = cardiac.selectedItems[category.category] || []
      if (selectedItemValues.length === 0 && status === 'unknown') {
        vendorSections.push({
          title: category.category,
          type: 'text',
          content: 'Status: Unknown',
        })
        continue
      }

      const items = selectedItemValues.map((itemValue) => {
        const item = category.items.find((i) => i.value === itemValue)
        let label = item?.label || itemValue

        // Sub-selections
        const subs = cardiac.subSelections[itemValue]
        if (subs && subs.length > 0 && item?.subItems) {
          const subLabels = subs
            .map((sv) => item.subItems?.find((si) => si.value === sv)?.label || sv)
            .join(', ')
          label += ` (${subLabels})`
        }

        // Text field
        const textVal = cardiac.textFields[itemValue]
        if (textVal) label += `: ${textVal}`

        return label
      })

      if (items.length > 0) {
        vendorSections.push({ title: category.category, type: 'list', items })
      }
    }
  }

  // Requested variants (family follow-up)
  if (form.requestedVariants) {
    vendorSections.push({
      title: 'Requested Variants (Family Follow-Up)',
      type: 'text',
      content: form.requestedVariants,
    })
  }

  // Family history
  if (!form.noFamilyHistory && form.familyHistory.length > 0) {
    const famRows = form.familyHistory
      .filter((r) => r.relationship || r.condition)
      .map((r) => [
        r.relationship,
        r.maternalPaternal || '-',
        r.condition,
        r.ageAtDiagnosis || 'N/A',
        r.additionalDetails || '-',
      ])
    if (famRows.length > 0) {
      vendorSections.push({
        title: 'Family History',
        type: 'table',
        headers: ['Relationship', 'Side', 'Condition', 'Age at Dx', 'Details'],
        rows: famRows,
      })
    }
  } else {
    const famText = form.patientIsAdopted
      ? 'Patient is adopted. Family history unavailable.'
      : 'No family history reported.'
    if (form.noFamilyHistory || form.patientIsAdopted) {
      vendorSections.push({ title: 'Family History', type: 'text', content: famText })
    }
  }

  // Race/ethnicity
  if (form.raceEthnicity.length > 0) {
    vendorSections.push({ title: 'Race/Ethnicity', type: 'list', items: form.raceEthnicity })
  }

  return {
    vendorName: 'Invitae (Labcorp Genetics)',
    orderTitle: 'Cardiology Requisition',
    patient: {
      firstName: auto.patient.firstName,
      middleName: auto.patient.middleName,
      lastName: auto.patient.lastName,
      dateOfBirth: auto.patient.dateOfBirth,
      sex: auto.patient.sexCode,
      email: auto.patient.email,
      phone: auto.patient.phone,
      address: auto.patient.address,
      city: auto.patient.city,
      state: auto.patient.state,
      zip: auto.patient.zip,
      country: auto.patient.country,
      patientId: auto.patient.patientId,
    },
    provider: {
      organizationName: auto.provider.organizationName,
      providerName: auto.provider.providerName,
      npi: auto.provider.npi,
      phone: auto.provider.orgPhone,
      fax: auto.provider.orgFax,
      address: auto.provider.orgAddress,
      city: auto.provider.orgCity,
      state: auto.provider.orgState,
      zip: auto.provider.orgZip,
      email: auto.provider.providerEmail,
      accountNumber: auto.provider.labAccountNumbers[LAB_ACCOUNT_SYSTEMS.INVITAE] || '',
    },
    insurance: {
      primaryCarrier: auto.insurance.primaryCarrier,
      primaryMemberId: auto.insurance.primaryMemberId,
      primaryGroup: auto.insurance.primaryGroup,
      policyholderName: auto.insurance.primaryCarrier ? auto.insurance.policyholderName : '',
      relationship: auto.insurance.primaryCarrier ? auto.insurance.relationship : '',
    },
    icd10Codes: form.diagnosisText
      ? form.diagnosisText
          .split(/[;\n]/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    selectedTests,
    vendorSections,
    signatureDataUrl: form.signatureDataUrl,
    signatureDate: form.signatureDate,
  }
}
